/**
 * Data Uploads — the generic-ingestion frontend (Wave G1 Sonnet S4).
 *
 * Upload a spend CSV (or link a public Google Sheet) -> map columns to
 * target fields -> land. Validation happens at mapping time and fails
 * closed (WAVE-G1-DESIGN.md §5): a single bad row rejects the whole
 * upload with row-addressed errors and nothing lands. Those errors are
 * the product here, not an afterthought — they render as a prominent,
 * addressed table, not a toast.
 *
 * Also hosts manual spend entry: a one-row upload for spend with no file
 * behind it (a contracted sponsorship, a TV buy, an invoice).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  DataTable,
  DropZone,
  FormLayout,
  InlineStack,
  Page,
  Select,
  SkeletonBodyText,
  SkeletonPage,
  Tabs,
  Text,
  TextField,
} from '@shopify/polaris';

import {
  createCsvUpload,
  createManualSpendEntry,
  createSheetLinkUpload,
  deleteManualSpendEntry,
  IngestionValidationError,
  listManualSpend,
  listUploads,
  mapUpload,
  updateManualSpendEntry,
} from '../services/dataUploadsApi';
import {
  SUPPORTED_CURRENCIES,
  TARGET_FIELDS,
  TARGET_KIND_OPTIONS,
  type ManualSpendEntryRecord,
  type RowError,
  type TargetFieldDef,
  type TargetKind,
  type UploadCreated,
  type UploadStatus,
  type UploadSummary,
} from '../types/dataUploads';

const INTAKE_TABS = [
  { id: 'csv-upload', content: 'Upload CSV' },
  { id: 'sheet-link', content: 'Link a Google Sheet' },
];

/** First preview row's value for a column — defensive against either a
 * {column: value} object shape or a positional array shape, since the
 * contract does not pin the preview_rows shape down. Sample display only;
 * never load-bearing for the actual mapping. */
function sampleValueFor(
  column: string,
  index: number,
  previewRows: UploadCreated['preview_rows']
): string {
  const first = previewRows?.[0];
  if (first == null) return '';
  if (Array.isArray(first)) {
    const value = first[index];
    return value == null ? '' : String(value);
  }
  const value = (first as Record<string, unknown>)[column];
  return value == null ? '' : String(value);
}

function statusTone(
  status: UploadStatus
): 'success' | 'critical' | 'info' | 'attention' | undefined {
  switch (status) {
    case 'landed':
      return 'success';
    case 'failed':
      return 'critical';
    case 'validated':
    case 'mapped':
      return 'info';
    case 'received':
      return 'attention';
    default:
      return undefined;
  }
}

export default function DataUploads() {
  const [loading, setLoading] = useState(true);

  // --- Intake (upload dropzone / sheet-link tab) ---
  const [intakeTab, setIntakeTab] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [refreshIntervalMinutes, setRefreshIntervalMinutes] = useState('');
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [intakeError, setIntakeError] = useState<string | null>(null);

  // --- Mapping step ---
  const [pendingUpload, setPendingUpload] = useState<UploadCreated | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [targetKind, setTargetKind] = useState<TargetKind>('spend');
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [defaults, setDefaults] = useState<Record<string, string | boolean>>({});
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [mapBusy, setMapBusy] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapResult, setMapResult] = useState<
    | { status: 'landed'; rowsLanded: number }
    | { status: 'failed'; errors: RowError[] }
    | null
  >(null);

  // --- Upload history ---
  const [uploads, setUploads] = useState<UploadSummary[]>([]);
  const [uploadsError, setUploadsError] = useState<string | null>(null);

  // --- Manual spend ---
  const [manualEditingId, setManualEditingId] = useState<string | null>(null);
  const [channelLabel, setChannelLabel] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [singleDay, setSingleDay] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [isCredit, setIsCredit] = useState(false);
  const [notes, setNotes] = useState('');
  const [manualBusy, setManualBusy] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualNotice, setManualNotice] = useState<string | null>(null);
  const [manualFieldErrors, setManualFieldErrors] = useState<Record<string, string>>({});
  const [entries, setEntries] = useState<ManualSpendEntryRecord[]>([]);
  const [entriesError, setEntriesError] = useState<string | null>(null);

  const loadUploads = useCallback(async () => {
    try {
      const data = await listUploads();
      setUploads(data.uploads);
      setUploadsError(null);
    } catch (e) {
      setUploadsError(e instanceof Error ? e.message : 'Failed to load uploads');
    }
  }, []);

  const loadEntries = useCallback(async () => {
    try {
      const data = await listManualSpend();
      setEntries(data.entries);
      setEntriesError(null);
    } catch (e) {
      setEntriesError(
        e instanceof Error ? e.message : 'Failed to load manual spend entries'
      );
    }
  }, []);

  useEffect(() => {
    // allSettled: an uploads-history hiccup must not hide the manual-spend
    // section (and vice versa) — same partial-degradation shape as the
    // rest of the app (see MarketingAgentDetail).
    void Promise.allSettled([loadUploads(), loadEntries()]).then(() =>
      setLoading(false)
    );
  }, [loadUploads, loadEntries]);

  // --- Intake handlers ---

  const beginMapping = (upload: UploadCreated, file: File | null) => {
    // A matched template re-applies WHOLE: its defaults commonly carry the
    // fixed platform label, and its target_kind decides which validator
    // runs. Seeding only the column map dropped both, so a saved spend
    // template came back as a bare 'spend'/{} and a file whose label lives
    // in defaults re-uploaded with no label at all.
    const template = upload.suggested_template ?? null;
    setPendingUpload(upload);
    setPendingFile(file);
    setTargetKind(template?.target_kind ?? 'spend');
    setColumnMap(template?.column_map ?? upload.suggested_mapping ?? {});
    setDefaults(template?.defaults ?? {});
    setSaveTemplate(false);
    setTemplateName('');
    setMapResult(null);
    setMapError(null);
  };

  const submitCsvUpload = async () => {
    if (!csvFile) {
      setIntakeError('Choose a CSV file first.');
      return;
    }
    setIntakeBusy(true);
    setIntakeError(null);
    try {
      const result = await createCsvUpload(csvFile);
      beginMapping(result, csvFile);
    } catch (e) {
      // A row-addressed 422 is renderable data, not a transport
      // failure — intake errors all carry row_number 0, so the
      // messages read better as a list than as a row table.
      setIntakeError(
        e instanceof IngestionValidationError
          ? e.errors.map((err) => err.message).join('; ') || e.message
          : e instanceof Error
            ? e.message
            : 'Upload failed'
      );
    } finally {
      setIntakeBusy(false);
    }
  };

  const submitSheetLink = async () => {
    if (!sheetUrl.trim()) {
      setIntakeError('Enter a Google Sheet link.');
      return;
    }
    setIntakeBusy(true);
    setIntakeError(null);
    try {
      // Blank means "one-time read" (the field's own help text). A
      // NONBLANK value that is not a positive whole number is a mistake the
      // user should see: silently coercing it to undefined created the
      // schedule they asked for as a one-time import instead, and the field
      // kept showing what they typed.
      const raw = refreshIntervalMinutes.trim();
      const parsed = Number(raw);
      if (raw && (!Number.isFinite(parsed) || parsed <= 0)) {
        setIntakeError(
          'Refresh interval must be a positive number of minutes, or blank for a one-time read.'
        );
        setIntakeBusy(false);
        return;
      }
      const minutes = raw ? parsed : undefined;
      const result = await createSheetLinkUpload(sheetUrl.trim(), minutes);
      // No local File exists for a sheet link — the server re-fetches the
      // stored source URL itself at map time (see mapUpload's doc comment).
      beginMapping(result, null);
    } catch (e) {
      // A row-addressed 422 is renderable data, not a transport
      // failure — intake errors all carry row_number 0, so the
      // messages read better as a list than as a row table.
      setIntakeError(
        e instanceof IngestionValidationError
          ? e.errors.map((err) => err.message).join('; ') || e.message
          : e instanceof Error
            ? e.message
            : 'Could not read that sheet'
      );
    } finally {
      setIntakeBusy(false);
    }
  };

  // --- Mapping handlers ---

  const fieldDefs = TARGET_FIELDS[targetKind];
  const mappedFields = new Set(Object.values(columnMap).filter(Boolean));

  const hasDefaultValue = (field: string) => {
    const v = defaults[field];
    return typeof v === 'boolean' ? v : typeof v === 'string' && v.trim() !== '';
  };

  const missingRequired = fieldDefs.filter(
    (f) => f.required && !mappedFields.has(f.field) && !hasDefaultValue(f.field)
  );

  const canSubmitMap =
    !!pendingUpload &&
    missingRequired.length === 0 &&
    !mapBusy &&
    (!saveTemplate || templateName.trim() !== '');

  const setColumnTarget = (column: string, field: string) => {
    setColumnMap((prev) => ({ ...prev, [column]: field }));
  };

  const setDefaultValue = (field: string, value: string | boolean) => {
    setDefaults((prev) => ({ ...prev, [field]: value }));
  };

  const stringDefault = (field: string): string => {
    const v = defaults[field];
    return typeof v === 'string' ? v : '';
  };

  const changeTargetKind = (kind: TargetKind) => {
    setTargetKind(kind);
    // Dropping fields that don't exist for the new kind stops a stale
    // mapping/default from silently riding along in the submitted payload.
    const allowed = new Set(TARGET_FIELDS[kind].map((f) => f.field));
    setColumnMap((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([col, field]) => [
          col,
          allowed.has(field) ? field : '',
        ])
      )
    );
    setDefaults((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([field]) => allowed.has(field)))
    );
  };

  const cancelMapping = () => {
    setPendingUpload(null);
    setPendingFile(null);
    setCsvFile(null);
    setMapResult(null);
    setMapError(null);
  };

  const submitMapping = async () => {
    if (!pendingUpload) return;
    setMapBusy(true);
    setMapError(null);
    try {
      const columnMapPayload = Object.fromEntries(
        Object.entries(columnMap).filter(([, field]) => field)
      );
      const defaultsPayload = Object.fromEntries(
        Object.entries(defaults).filter(([, v]) =>
          typeof v === 'boolean' ? v : v.trim() !== ''
        )
      );
      const result = await mapUpload({
        uploadId: pendingUpload.upload_id,
        file: pendingFile,
        targetKind,
        columnMap: columnMapPayload,
        defaults: defaultsPayload,
        saveAsTemplate: saveTemplate ? templateName.trim() : undefined,
      });
      setMapResult({ status: 'landed', rowsLanded: result.rows_landed });
      setPendingUpload(null);
      setPendingFile(null);
      setCsvFile(null);
      setSheetUrl('');
      await loadUploads();
    } catch (e) {
      if (e instanceof IngestionValidationError) {
        // Fail closed: nothing landed. Keep the mapping step open (same
        // file, same column choices) so the user can fix and resubmit
        // without re-uploading.
        setMapResult({ status: 'failed', errors: e.errors });
      } else {
        setMapError(e instanceof Error ? e.message : 'Mapping failed');
      }
    } finally {
      setMapBusy(false);
    }
  };

  const renderDefaultInput = (f: TargetFieldDef) => {
    const label = f.required ? `${f.label} *` : f.label;
    if (f.inputKind === 'boolean') {
      return (
        <Checkbox
          label={label}
          checked={defaults[f.field] === true}
          onChange={(checked) => setDefaultValue(f.field, checked)}
        />
      );
    }
    if (f.inputKind === 'currency') {
      return (
        <Select
          label={label}
          options={[
            { label: 'Choose a currency…', value: '' },
            ...SUPPORTED_CURRENCIES.map((c) => ({ label: c, value: c })),
          ]}
          value={stringDefault(f.field)}
          onChange={(value) => setDefaultValue(f.field, value)}
        />
      );
    }
    return (
      <TextField
        label={label}
        type={
          f.inputKind === 'date' ? 'date' : f.inputKind === 'number' ? 'number' : 'text'
        }
        value={stringDefault(f.field)}
        onChange={(value) => setDefaultValue(f.field, value)}
        autoComplete="off"
      />
    );
  };

  // --- Manual spend handlers ---

  const resetManualForm = () => {
    setManualEditingId(null);
    setChannelLabel('');
    setPeriodStart('');
    setPeriodEnd('');
    setSingleDay(false);
    setAmount('');
    setCurrency('USD');
    setIsCredit(false);
    setNotes('');
    setManualFieldErrors({});
  };

  const canSubmitManual =
    channelLabel.trim() !== '' &&
    periodStart !== '' &&
    (singleDay || periodEnd !== '') &&
    amount.trim() !== '' &&
    Number.isFinite(Number(amount));

  const submitManual = async () => {
    setManualBusy(true);
    setManualError(null);
    setManualNotice(null);
    setManualFieldErrors({});
    const input = {
      channel_label: channelLabel.trim(),
      period_start: periodStart,
      period_end: singleDay ? periodStart : periodEnd,
      amount: Number(amount),
      currency,
      is_credit: isCredit,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    try {
      if (manualEditingId) {
        await updateManualSpendEntry(manualEditingId, input);
        setManualNotice('Entry updated.');
      } else {
        await createManualSpendEntry(input);
        setManualNotice('Entry added.');
      }
      resetManualForm();
      await loadEntries();
    } catch (e) {
      if (e instanceof IngestionValidationError) {
        const fieldErrors: Record<string, string> = {};
        e.errors.forEach((err) => {
          fieldErrors[err.field] = err.message;
        });
        setManualFieldErrors(fieldErrors);
        setManualError(
          e.errors.map((err) => `${err.field}: ${err.message}`).join(' ')
        );
      } else {
        setManualError(e instanceof Error ? e.message : 'Save failed');
      }
    } finally {
      setManualBusy(false);
    }
  };

  const startEdit = (entry: ManualSpendEntryRecord) => {
    setManualEditingId(entry.id);
    setChannelLabel(entry.channel_label);
    setPeriodStart(entry.period_start);
    setPeriodEnd(entry.period_end);
    setSingleDay(entry.period_start === entry.period_end);
    setAmount(String(entry.amount));
    setCurrency(entry.currency);
    setIsCredit(entry.is_credit);
    setNotes(entry.notes ?? '');
    setManualFieldErrors({});
    setManualError(null);
    setManualNotice(null);
  };

  const removeEntry = async (entry: ManualSpendEntryRecord) => {
    if (!window.confirm(`Delete the manual spend entry "${entry.channel_label}"?`)) {
      return;
    }
    try {
      await deleteManualSpendEntry(entry.id);
      if (manualEditingId === entry.id) resetManualForm();
      await loadEntries();
    } catch (e) {
      setEntriesError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  if (loading) {
    return (
      <SkeletonPage title="Data Uploads">
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      </SkeletonPage>
    );
  }

  return (
    <Page
      title="Data Uploads"
      subtitle="Bring spend in without a native connector — upload a CSV, link a sheet, or enter it by hand. Validation runs at mapping time: a bad row rejects the whole file, nothing lands silently."
    >
      <BlockStack gap="500">
        {mapResult?.status === 'landed' && (
          <Banner tone="success" onDismiss={() => setMapResult(null)}>
            <p>
              Landed {mapResult.rowsLanded} row
              {mapResult.rowsLanded === 1 ? '' : 's'}.
            </p>
          </Banner>
        )}

        {!pendingUpload && (
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Add data
              </Text>
              {intakeError && (
                <Banner tone="critical" onDismiss={() => setIntakeError(null)}>
                  {intakeError}
                </Banner>
              )}
              <Tabs tabs={INTAKE_TABS} selected={intakeTab} onSelect={setIntakeTab}>
                <Box paddingBlockStart="400">
                  {intakeTab === 0 ? (
                    <BlockStack gap="300">
                      <DropZone
                        label="Spend CSV"
                        accept=".csv"
                        type="file"
                        allowMultiple={false}
                        onDropAccepted={(accepted) => {
                          setCsvFile(accepted[0]);
                          setIntakeError(null);
                        }}
                        onDropRejected={() =>
                          setIntakeError('That file is not a CSV.')
                        }
                      >
                        {csvFile ? (
                          <Box padding="400">
                            <Text as="p" fontWeight="semibold">
                              {csvFile.name}
                            </Text>
                            <Text as="p" tone="subdued">
                              {(csvFile.size / 1024).toFixed(1)} KB
                            </Text>
                          </Box>
                        ) : (
                          <DropZone.FileUpload actionHint="Accepts .csv files" />
                        )}
                      </DropZone>
                      <InlineStack gap="200">
                        <Button
                          variant="primary"
                          onClick={() => void submitCsvUpload()}
                          loading={intakeBusy}
                          disabled={!csvFile}
                        >
                          Upload &amp; continue
                        </Button>
                        {csvFile && (
                          <Button
                            onClick={() => setCsvFile(null)}
                            disabled={intakeBusy}
                          >
                            Choose a different file
                          </Button>
                        )}
                      </InlineStack>
                    </BlockStack>
                  ) : (
                    <BlockStack gap="300">
                      <TextField
                        label="Google Sheet link"
                        value={sheetUrl}
                        onChange={setSheetUrl}
                        autoComplete="off"
                        placeholder="https://docs.google.com/spreadsheets/d/…/pub?output=csv"
                        helpText="A link-shared sheet's public CSV export URL. No OAuth — G1 ships CSV-first."
                      />
                      <TextField
                        label="Refresh interval, minutes (optional)"
                        type="number"
                        value={refreshIntervalMinutes}
                        onChange={setRefreshIntervalMinutes}
                        autoComplete="off"
                        helpText="How often to re-fetch this sheet. Leave blank for a one-time read."
                      />
                      <InlineStack>
                        <Button
                          variant="primary"
                          onClick={() => void submitSheetLink()}
                          loading={intakeBusy}
                          disabled={!sheetUrl.trim()}
                        >
                          Read sheet &amp; continue
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  )}
                </Box>
              </Tabs>
            </BlockStack>
          </Card>
        )}

        {pendingUpload && (
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Map columns
                </Text>
                <Text as="p" tone="subdued">
                  {pendingUpload.kind === 'csv' ? 'File' : 'Sheet'}:{' '}
                  {pendingFile?.name ?? sheetUrl}
                </Text>
              </BlockStack>

              {mapError && (
                <Banner tone="critical" onDismiss={() => setMapError(null)}>
                  {mapError}
                </Banner>
              )}

              {mapResult?.status === 'failed' && (
                <BlockStack gap="200">
                  <Banner tone="critical">
                    <p>
                      {mapResult.errors.length} row
                      {mapResult.errors.length === 1 ? '' : 's'} failed validation
                      — nothing was landed. Fix the mapping or the file and try
                      again.
                    </p>
                  </Banner>
                  <DataTable
                    columnContentTypes={['numeric', 'text', 'text', 'text']}
                    headings={['Row', 'Field', 'Code', 'Message']}
                    rows={mapResult.errors.map((err) => [
                      err.row_number,
                      err.field,
                      err.code,
                      err.message,
                    ])}
                  />
                </BlockStack>
              )}

              <Select
                label="This file contains"
                options={TARGET_KIND_OPTIONS}
                value={targetKind}
                onChange={(value) => changeTargetKind(value as TargetKind)}
              />

              {pendingUpload.columns.length ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['CSV column', 'Sample value', 'Maps to']}
                  rows={pendingUpload.columns.map((col, idx) => [
                    col,
                    sampleValueFor(col, idx, pendingUpload.preview_rows),
                    (
                      <Select
                        key={col}
                        label={`Maps to, for column ${col}`}
                        labelHidden
                        options={[
                          { label: "Don't import", value: '' },
                          ...fieldDefs.map((f) => ({
                            label: f.required ? `${f.label} *` : f.label,
                            value: f.field,
                          })),
                        ]}
                        value={columnMap[col] ?? ''}
                        onChange={(value) => setColumnTarget(col, value)}
                      />
                    ) as unknown as string,
                  ])}
                />
              ) : (
                <Text as="p" tone="subdued">
                  This file has no columns to map.
                </Text>
              )}

              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Fixed values (optional)
                </Text>
                <Text as="p" tone="subdued">
                  For any field not mapped from a column, set one fixed value for
                  the whole file — e.g. a single platform label. Required fields
                  marked * must be either mapped or given a fixed value.
                </Text>
                {fieldDefs
                  .filter((f) => !mappedFields.has(f.field))
                  .map((f) => <Box key={f.field}>{renderDefaultInput(f)}</Box>)}
              </BlockStack>

              <Checkbox
                label="Save this mapping as a template for future uploads"
                checked={saveTemplate}
                onChange={setSaveTemplate}
              />
              {saveTemplate && (
                <TextField
                  label="Template name"
                  value={templateName}
                  onChange={setTemplateName}
                  autoComplete="off"
                  placeholder="e.g. Monthly TV spend report"
                />
              )}

              <InlineStack gap="200">
                <Button onClick={cancelMapping} disabled={mapBusy}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void submitMapping()}
                  loading={mapBusy}
                  disabled={!canSubmitMap}
                >
                  Land data
                </Button>
              </InlineStack>
              {missingRequired.length > 0 && (
                <Text as="p" tone="subdued">
                  Map a column or set a fixed value for:{' '}
                  {missingRequired.map((f) => f.label).join(', ')}.
                </Text>
              )}
            </BlockStack>
          </Card>
        )}

        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingMd">
                Upload history
              </Text>
              <Button size="slim" onClick={() => void loadUploads()}>
                Refresh
              </Button>
            </InlineStack>
            {uploadsError && <Banner tone="critical">{uploadsError}</Banner>}
            {uploads.length ? (
              <DataTable
                columnContentTypes={[
                  'text',
                  'text',
                  'text',
                  'numeric',
                  'numeric',
                  'numeric',
                  'text',
                ]}
                headings={[
                  'Source',
                  'Kind',
                  'Status',
                  'Received',
                  'Landed',
                  'Rejected',
                  'Uploaded',
                ]}
                rows={uploads.map((u) => [
                  u.source_ref,
                  u.kind === 'csv' ? 'CSV' : 'Sheet link',
                  (
                    <Badge key={u.id} tone={statusTone(u.status)}>
                      {u.status}
                    </Badge>
                  ) as unknown as string,
                  u.rows_received,
                  u.rows_landed,
                  u.rows_rejected,
                  new Date(u.created_at).toLocaleString(),
                ])}
              />
            ) : (
              <Text as="p" tone="subdued">
                No uploads yet.
              </Text>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                Manual spend entry
              </Text>
              <Text as="p" tone="subdued">
                For spend with no file behind it — a contracted sponsorship, a TV
                buy, an invoice. One row is one entry.
              </Text>
            </BlockStack>

            {manualError && (
              <Banner tone="critical" onDismiss={() => setManualError(null)}>
                {manualError}
              </Banner>
            )}
            {manualNotice && (
              <Banner tone="success" onDismiss={() => setManualNotice(null)}>
                {manualNotice}
              </Banner>
            )}

            <FormLayout>
              <TextField
                label="Channel / platform label"
                value={channelLabel}
                onChange={setChannelLabel}
                autoComplete="off"
                placeholder="tv, podcast_sponsorship, meta_ads…"
                error={manualFieldErrors.channel_label}
              />
              <FormLayout.Group>
                <TextField
                  label="Period start"
                  type="date"
                  value={periodStart}
                  onChange={setPeriodStart}
                  autoComplete="off"
                  error={manualFieldErrors.period_start}
                />
                {!singleDay && (
                  <TextField
                    label="Period end"
                    type="date"
                    value={periodEnd}
                    onChange={setPeriodEnd}
                    autoComplete="off"
                    error={manualFieldErrors.period_end}
                  />
                )}
              </FormLayout.Group>
              <Checkbox
                label="Single day (period end = period start)"
                checked={singleDay}
                onChange={setSingleDay}
              />
              <FormLayout.Group>
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={setAmount}
                  autoComplete="off"
                  error={manualFieldErrors.amount}
                />
                <Select
                  label="Currency"
                  options={SUPPORTED_CURRENCIES.map((c) => ({ label: c, value: c }))}
                  value={currency}
                  onChange={setCurrency}
                  error={manualFieldErrors.currency}
                />
              </FormLayout.Group>
              <Checkbox
                label="This is a credit (reduces spend — refund or rebate)"
                checked={isCredit}
                onChange={setIsCredit}
                helpText="Required whenever the amount represents money coming back, not going out."
              />
              <TextField
                label="Notes (optional)"
                value={notes}
                onChange={setNotes}
                multiline={2}
                autoComplete="off"
              />
              <InlineStack gap="200">
                <Button
                  variant="primary"
                  onClick={() => void submitManual()}
                  loading={manualBusy}
                  disabled={!canSubmitManual}
                >
                  {manualEditingId ? 'Save changes' : 'Add entry'}
                </Button>
                {manualEditingId && (
                  <Button onClick={resetManualForm} disabled={manualBusy}>
                    Cancel edit
                  </Button>
                )}
              </InlineStack>
            </FormLayout>

            {entriesError && <Banner tone="critical">{entriesError}</Banner>}
            {entries.length ? (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                headings={['Channel', 'Period', 'Amount', 'Notes', '']}
                rows={entries.map((entry) => [
                  entry.channel_label,
                  entry.period_start === entry.period_end
                    ? entry.period_start
                    : `${entry.period_start} – ${entry.period_end}`,
                  `${entry.is_credit ? '−' : ''}${entry.amount} ${entry.currency}`,
                  entry.notes ?? '',
                  (
                    <InlineStack gap="100" key={entry.id}>
                      <Button size="micro" onClick={() => startEdit(entry)}>
                        Edit
                      </Button>
                      <Button
                        size="micro"
                        tone="critical"
                        onClick={() => void removeEntry(entry)}
                      >
                        Delete
                      </Button>
                    </InlineStack>
                  ) as unknown as string,
                ])}
              />
            ) : (
              <Text as="p" tone="subdued">
                No manual spend entries yet.
              </Text>
            )}
          </BlockStack>
        </Card>

        <Box paddingBlockEnd="400" />
      </BlockStack>
    </Page>
  );
}
